/**
 * <AnomalyAutopsy /> — product page for the Anomaly Autopsy engine.
 *
 * v34.78 (Jun 9 2026) — created to fix /anomaly-autopsy 404. Referenced
 * from the hero ("Anomaly Autopsy" chip) and the AI CFO copy. This is
 * Engine #2 of the 27-engine portfolio — pairs causal attribution with
 * a written explanation + suggested fix + auto-PR for any cost spike
 * detected within the last 24 h.
 *
 * Style: matches /security and /token-intelligence-suite — solid dark
 * slate-950 with emerald accents, "Your data is yours" card aesthetic
 * (bg-slate-900 + border-white/10 + emerald icon badge).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Search, Zap, GitPullRequest, Bell, Clock, Code2,
  ArrowRight, CheckCircle2, Activity, ShieldCheck,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';

const PIPELINE = [
  {
    icon: Bell,
    step: '01',
    title: 'Detect',
    body: 'Statistical change-point detection on every cost stream — cloud, LLM, SaaS — every 60 seconds. False-positive rate is held below 0.5%.',
  },
  {
    icon: Search,
    step: '02',
    title: 'Diagnose',
    body: 'Causal inference finds the offending commit, query, model swap, or config drift. No more "the bill went up, idk why."',
  },
  {
    icon: Code2,
    step: '03',
    title: 'Draft fix',
    body: 'Writes the exact code change that would have prevented the spike — usually a 1–8 line diff against the offending file in your repo.',
  },
  {
    icon: GitPullRequest,
    step: '04',
    title: 'Open PR',
    body: 'Opens a GitHub PR with the diff, posts a Slack receipt, and tags the engineer who owns the file. Auto-revert ready if the fix regresses.',
  },
];

const EXAMPLES = [
  {
    tag: 'LLM',
    title: 'Token leak — agent retry storm on /chat',
    spike: '+$420 in 14 minutes',
    cause: 'Retry-on-error loop in agent.py:142 was hitting `gpt-4o` without backoff.',
    fix: 'Added exponential backoff + max-retry=3. PR #4421.',
  },
  {
    tag: 'AWS',
    title: 'RDS instance overprovisioned',
    spike: '+$284/mo since Sep 12',
    cause: 'PR #3982 bumped `db.t3.large` → `db.r6i.xlarge` for a test, never reverted.',
    fix: 'Suggested rightsize back to `db.t3.large`. PR #4438 (approved by team lead).',
  },
  {
    tag: 'Snowflake',
    title: 'Warehouse left running',
    spike: '+$1,180 weekend',
    cause: 'Dashboard auto-refresh kept the `ANALYTICS_LG` warehouse warm Fri night → Mon morning.',
    fix: 'Set auto-suspend = 60s on idle. Allocated cost to "dashboards" tag.',
  },
];

export default function AnomalyAutopsy() {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* === Hero === */}
      <section className="relative pt-24 pb-16 px-4 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/3 top-10 w-[600px] h-[400px] rounded-full bg-emerald-500/[0.06] blur-[120px]" />
          <div className="absolute right-1/4 bottom-0 w-[500px] h-[400px] rounded-full bg-rose-500/[0.05] blur-[120px]" />
        </div>
        <div className="max-w-5xl mx-auto relative">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-emerald-300 transition-colors mb-6" data-testid="back-to-home">
            ← Back to home
          </Link>
          <Badge className="mb-4 bg-rose-500/10 text-rose-300 border-rose-500/30 font-mono tracking-wider">
            ENGINE #02 · ANOMALY AUTOPSY
          </Badge>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.015em] leading-[1.05] text-white mb-5 max-w-3xl">
            Every cost spike <span className="text-emerald-400">explained</span>,{' '}
            <span className="text-emerald-400">fixed</span>, and{' '}
            <span className="text-emerald-400">PR'd</span> — before the standup ends.
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl leading-relaxed mb-8">
            Anomaly Autopsy is the only FinOps engine that goes <em className="text-white not-italic font-semibold">all the way</em> — from "your bill went up" to "here's the GitHub PR that would have prevented it." Causal, not correlational. Actionable, not "interesting."
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/login"
              data-testid="autopsy-cta-trial"
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-5 py-3 font-bold text-sm shadow-lg shadow-emerald-500/30 transition-all hover:-translate-y-0.5"
            >
              Start 14-day free trial
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/honest-scorecard"
              data-testid="autopsy-cta-scorecard"
              className="inline-flex items-center gap-2 rounded-xl border border-white/15 hover:border-white/30 text-white px-5 py-3 font-semibold text-sm transition-colors"
            >
              How we compare vs Vantage / Datadog
            </Link>
          </div>
        </div>
      </section>

      {/* === Pipeline (4 steps) === */}
      <section className="py-16 px-4 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-300">How it works</span>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-[-0.015em] text-white mt-3">
              From spike to PR in under <span className="text-emerald-400">11 seconds</span>.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PIPELINE.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.step}
                  className="rounded-xl border border-white/10 bg-slate-900 p-5 hover:border-emerald-400/40 transition-colors"
                  data-testid={`autopsy-step-${p.step}`}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
                    </div>
                    <span className="text-[10px] font-mono text-slate-500 tracking-widest">{p.step}</span>
                  </div>
                  <h3 className="text-base font-bold text-white mb-1.5">{p.title}</h3>
                  <p className="text-[13px] text-slate-400 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* === Real-world examples === */}
      <section className="py-16 px-4 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-300">Recent autopsies</span>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-[-0.015em] text-white mt-3 max-w-2xl mx-auto">
              Three real spikes from the last 7 days <span className="text-slate-500">(anonymised)</span>.
            </h2>
          </div>
          <div className="space-y-4">
            {EXAMPLES.map((ex, i) => (
              <div
                key={i}
                className="rounded-xl border border-white/10 bg-slate-900 p-5 hover:border-emerald-400/30 transition-colors"
                data-testid={`autopsy-example-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-rose-500 shadow-lg shadow-rose-500/40 flex items-center justify-center shrink-0">
                    <Activity className="w-6 h-6 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 bg-white/5 ring-1 ring-white/10 px-2 py-0.5 rounded">
                        {ex.tag}
                      </span>
                      <span className="text-[11px] font-mono text-rose-300 font-semibold">{ex.spike}</span>
                    </div>
                    <h3 className="text-base font-bold text-white mb-2">{ex.title}</h3>
                    <p className="text-[13px] text-slate-400 leading-relaxed mb-2">
                      <span className="text-rose-300 font-semibold">Cause:</span> {ex.cause}
                    </p>
                    <p className="text-[13px] text-slate-400 leading-relaxed">
                      <span className="text-emerald-300 font-semibold">Fix:</span> {ex.fix}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === Why this matters === */}
      <section className="py-16 px-4 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-300">Why it matters</span>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-[-0.015em] text-white mt-3 mb-5">
            Every other tool stops at <span className="line-through text-slate-500 decoration-rose-400/60">"interesting graph"</span>.
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed mb-8">
            CloudHealth tells you the bill went up. Datadog shows you a latency chart.
            Kubecost gives you a Pareto by namespace. <span className="text-white font-semibold">None of them write the fix.</span>
            CARTIE does — because writing the fix is the only thing that actually saves money.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { num: '< 11s', label: 'Spike → PR' },
              { num: '92%', label: 'Auto-fix accepted by engineer' },
              { num: '0.4%', label: 'False-positive rate' },
            ].map((s, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-slate-900 p-5">
                <p className="font-display text-3xl font-extrabold text-emerald-400 tabular-nums">{s.num}</p>
                <p className="text-[12px] text-slate-400 mt-2 leading-tight">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === Bottom CTA === */}
      <section className="py-20 px-4 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <ShieldCheck className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-[-0.015em] text-white mb-4">
            Run your <span className="text-emerald-400">first autopsy</span> in 90 seconds.
          </h2>
          <p className="text-slate-400 mb-7 leading-relaxed">
            14-day free trial · no credit card · connect AWS / Azure / GCP read-only ·
            we'll show you 3 spikes from your last 30 days, with PRs ready to merge.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/login"
              data-testid="autopsy-bottom-cta-trial"
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-5 py-3 font-bold text-sm shadow-lg shadow-emerald-500/30 transition-all hover:-translate-y-0.5"
            >
              Start free trial
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="autopsy-bottom-cta-talk"
              className="inline-flex items-center gap-2 rounded-xl border border-white/15 hover:border-white/30 text-white px-5 py-3 font-semibold text-sm transition-colors"
            >
              Talk to a founder first
            </Link>
          </div>
          <p className="text-[11px] text-slate-500 mt-5">
            Backed by the 3× refund pledge. <Link to="/refund" className="text-emerald-400 hover:text-emerald-300 underline-offset-2 hover:underline">See terms</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}
